import React, { useEffect, useRef, useState } from 'react';
import type { GlobalProps } from '@bloomberg/media-ui-core';
import { classes, NotificationBanner } from '@bloomberg/media-ui-core';
import RegUIClient, {
  CaptchaType,
  RegUIClientSocialLoginResponse,
} from 'reg-ui-client';

import { EmailForm } from '../EmailForm';
import { EnterPasswordOrVerificationCode } from '../EnterPasswordOrVerificationCode';
import { defaultMessages, LABEL_KEYS, useTranslateMessage } from '../i18n';
import { RegGoogleTracking, RegNewRelicLogging } from '../utils/AnalyticsUtils';
import {
  setButtonTypeFromVariant,
  setThemeFromVariant,
} from '../utils/helpers';
import { LoginRegistrationErrors } from '../utils/interfaces';
import { onEmailLogin } from '../utils/regUIHelpers';

import {
  CodeInputHandle,
  VerificationCodeInput,
} from './VerificationCodeInput';

import styles from './styles.module.css';

export type Props = GlobalProps & {
  regUIClient?: RegUIClient;
  onEmailSubmit: (email: string, optIn: boolean, success: boolean) => void;
  /** this field is not for the verification code, but setting the user info from a login response */
  onVerificationCodeSubmit: (
    userDataResp: RegUIClientSocialLoginResponse
  ) => void;
  onPasswordLogin: (
    email: string,
    password: string,
    captcha: CaptchaType
  ) => Promise<void>;
  onTerminalLogin: (email: string, success: boolean) => void;
  defaultEmail?: string;
  countryCode?: string;
  allowEditEmail?: boolean;
  buttonText?: string;
  header?: JSX.Element | null;
  centeredLayout?: boolean;
  inlineButton?: boolean;
  autoFocus?: boolean;
  marketingConsent?: boolean;
  /** @deprecated use theme and buttonType */
  variant?: 'default' | 'dark';
  theme?: 'light' | 'dark';
  buttonType?: 'primary' | 'outlined';
  setGlobalMarketingConsent?: (marketingConsent: boolean) => void;
  setShowSocialSignins?: (show: boolean) => void;
  setIsLoggedIn?: (isLoggedIn: boolean) => void;
  googleTracking?: RegGoogleTracking;
  nr?: RegNewRelicLogging;
  error?: LoginRegistrationErrors;
  setError?: (error: LoginRegistrationErrors) => void;
  fullWidth?: boolean;
  allowPassword?: boolean;
  leadSource?: string;
};

enum LOGIN_STEP {
  EMAIL_SUBMISSION = 'EMAIL_SUBMISSION',
  CHOOSE_YOUR_METHOD = 'CHOOSE_YOUR_METHOD',
  VERIFICATION_CODE = 'VERIFICATION_CODE',
}

const DEBOUNCE_TIMEOUT_RESEND = 30000;

export const EmailVerificationForm = React.forwardRef<HTMLInputElement, Props>(
  function EmailVerificationForm(props: Props, ref) {
    const {
      onEmailSubmit,
      onTerminalLogin,
      defaultEmail,
      countryCode,
      allowEditEmail,
      buttonText,
      header,
      centeredLayout = true,
      inlineButton = true,
      autoFocus = false,
      marketingConsent,
      onPasswordLogin: onPasswordLoginParent,
      onVerificationCodeSubmit,
      setGlobalMarketingConsent,
      setIsLoggedIn,
      setShowSocialSignins,
      googleTracking,
      nr,
      error,
      setError,
      fullWidth = true,
      allowPassword = false,
      leadSource,
    } = props;

    if (!props.regUIClient) {
      // eslint-disable-next-line no-console
      console.warn(
        'Please pass in a regUIClient parameter from the client instead!'
      );
    }

    const theme = setThemeFromVariant(props.theme, props.variant);

    const buttonType = setButtonTypeFromVariant(
      props.buttonType,
      props.variant
    );

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [codeReSent, setCodeReSent] = useState<boolean>(false);
    const [loginStep, setLoginStep] = useState<LOGIN_STEP>(
      // eslint-disable-next-line no-nested-ternary
      defaultEmail
        ? allowPassword
          ? LOGIN_STEP.CHOOSE_YOUR_METHOD
          : LOGIN_STEP.VERIFICATION_CODE
        : LOGIN_STEP.EMAIL_SUBMISSION
    );
    const [emailToVerify, setEmailToVerify] = useState<string | undefined>(
      defaultEmail
    );
    const [currentEmail, setCurrentEmail] = useState<string | undefined>(
      defaultEmail
    );
    const [regUIClient, setRegUIClient] = useState<RegUIClient | undefined>(
      props.regUIClient
    );
    const [groupSSO, setGroupSSO] = useState<boolean>(false);
    const [verificationCodeSent, setVerificationCodeSent] =
      useState<boolean>(false);

    const translateMessage = useTranslateMessage();

    useEffect(() => {
      if (regUIClient) {
        return;
      }

      setRegUIClient(new RegUIClient());
    }, []);

    useEffect(() => {
      if (verificationCodeSent) {
        // Only show success banner when the code is sent successfully
        setCodeReSent(true);
        setTimeout(() => {
          setCodeReSent(false);
        }, DEBOUNCE_TIMEOUT_RESEND);
      }
    }, [verificationCodeSent]);

    const refCodeForm = useRef<CodeInputHandle>(null);

    const onPasswordLogin = async (
      email: string,
      password: string,
      captcha: CaptchaType
    ): Promise<void> => {
      setIsLoading(true);

      try {
        await onPasswordLoginParent(email, password, captcha);
      } catch (err) {
        const catchError = err as Error;

        nr?.logErrorEvent('Invalid Password Login', { error: catchError });
      } finally {
        setIsLoading(false);
      }
    };

    const onVerificationCodeInputStarting = () => {
      setIsLoading(true);
    };

    const onVerificationCodeInputComplete = () => {
      setIsLoading(false);
    };

    const onSendCode = async (email: string): Promise<boolean> => {
      refCodeForm.current?.clearForm();

      try {
        const userDataResp = await regUIClient?.sendVerificationEmail(email);

        if (!userDataResp?.success) {
          const rawError = userDataResp?.error?.message;
          const translatedError = rawError
            ? translateMessage(
                {
                  id: LABEL_KEYS['media-ui-login_sendCodeFailedWithError'],
                  defaultMessage:
                    defaultMessages['media-ui-login_sendCodeFailedWithError'],
                },
                { errorMessage: rawError }
              )
            : translateMessage({
                id: LABEL_KEYS['media-ui-login_sendCodeFailedPlain'],
                defaultMessage:
                  defaultMessages['media-ui-login_sendCodeFailedPlain'],
              });

          nr?.logErrorEvent('MediaUiLoginErrors', {
            errorMessage:
              rawError || defaultMessages['media-ui-login_sendCodeFailedPlain'],
            email,
            type: 'SEND_VERIFICATION_CODE',
          });

          setError?.({
            ...error,
            verificationCode: translatedError,
          });

          setVerificationCodeSent(false);

          return false;
        }
      } catch (err) {
        const catchError = err as Error;
        const rawError = catchError.message;
        const translatedError = rawError
          ? translateMessage(
              {
                id: LABEL_KEYS['media-ui-login_sendCodeFailedWithError'],
                defaultMessage:
                  defaultMessages['media-ui-login_sendCodeFailedWithError'],
              },
              { errorMessage: rawError }
            )
          : translateMessage({
              id: LABEL_KEYS['media-ui-login_sendCodeFailedPlain'],
              defaultMessage:
                defaultMessages['media-ui-login_sendCodeFailedPlain'],
            });

        nr?.logErrorEvent('MediaUiLoginErrors', {
          errorMessage:
            rawError || defaultMessages['media-ui-login_sendCodeFailedPlain'],
          email,
          type: 'SEND_VERIFICATION_CODE',
        });

        setError?.({
          ...error,
          verificationCode: translatedError,
        });

        setVerificationCodeSent(false);

        return false;
      }

      setVerificationCodeSent(true);

      return true;
    };

    /* c8 ignore next */
    const onEmailFormSubmit = async (
      email: string,
      optIn: boolean
    ): Promise<void> => {
      /* c8 ignore next */
      if (!regUIClient) {
        return;
      }
      // We only set optIn when a user is a new user so either firstTimeUser or accountWasCreated
      // isTerminalSubscriber checks all linked accounts if one of them is terminal, but that means the user exists already
      // so we won't set opt in anymore.
      // Therefore, the only thing that actually matters is if the current email is a bloomberg email if the user is new

      const isTerminal = email.endsWith('@bloomberg.net');

      setIsLoading(true);
      setGlobalMarketingConsent?.(optIn);

      const respInfo: undefined | RegUIClientSocialLoginResponse =
        await onEmailLogin(regUIClient, email);

      // if the user was able to login with terminal credentials
      // this will set their information and later leave this function
      if (respInfo?.success && respInfo?.data) {
        respInfo.data.email = email;
        onVerificationCodeSubmit(respInfo);
      }

      if (respInfo?.data?.isGroupSSO) {
        // No-op since this is a group sso user & we'll wait for the redirect to occur.
        // In the meanwhile, show the user a nice message
        setGroupSSO(true);
        setShowSocialSignins?.(false);
      } else if (isTerminal) {
        onTerminalLogin(email, Boolean(respInfo?.success));
      } else {
        setEmailToVerify(email);
        const sendCodeSuccess = await onSendCode(email);

        if (allowPassword) {
          // Always proceed to second step when password is allowed
          setLoginStep(LOGIN_STEP.CHOOSE_YOUR_METHOD);
          setShowSocialSignins?.(false);
        } else if (sendCodeSuccess) {
          // Only proceed if code was sent successfully when password is not allowed
          setLoginStep(LOGIN_STEP.VERIFICATION_CODE);
          setShowSocialSignins?.(false);
        } else {
          setError?.({
            ...error,
            email: translateMessage({
              id: LABEL_KEYS['media-ui-login_sendCodeFailed'],
              defaultMessage: defaultMessages['media-ui-login_sendCodeFailed'],
            }),
          });
        }
      }

      // Note: onEmailSubmit == onEmailCapture
      onEmailSubmit(
        email,
        (respInfo?.success && isTerminal) || optIn,
        Boolean(respInfo?.success)
      );

      setIsLoading(false);
    };

    useEffect(() => {
      if (!emailToVerify || !regUIClient) {
        return;
      }

      // if this component is loaded with an emailToVerify, it's intending to load on the
      // code entry screen, which means we need to immediately prompt the email for the
      // provided address.
      onEmailFormSubmit(emailToVerify, marketingConsent || false);
    }, [regUIClient]);

    /* c8 ignore next */
    const onResendEmail = async (): Promise<void> => {
      if (emailToVerify) {
        await onSendCode(emailToVerify);

        // we can't tell if this is login or register anymore, so default to register
        googleTracking?.trackRegisterEvent('resend_code');
        nr?.logRegisterEvent('resend_code');
      }
    };

    const changeEmail = (): void => {
      setEmailToVerify(undefined);
      setLoginStep(LOGIN_STEP.EMAIL_SUBMISSION);

      setShowSocialSignins?.(true);
    };

    if (!emailToVerify || loginStep === LOGIN_STEP.EMAIL_SUBMISSION) {
      return (
        <div className={classes([styles.verificationEmailFormWrapper])}>
          <div className={classes([styles.verificationEmailForm])}>
            {header && (
              <div
                className={classes([
                  styles.header,
                  {
                    [styles.textWhite]: theme === 'dark',
                  },
                ])}
              >
                {header}
              </div>
            )}
            {groupSSO ? (
              <NotificationBanner
                variant="success"
                title={translateMessage({
                  id: LABEL_KEYS['media-ui-login_redirectingGroupSso'],
                  defaultMessage:
                    defaultMessages['media-ui-login_redirectingGroupSso'],
                })}
              />
            ) : (
              <EmailForm
                inlineButton={inlineButton}
                onFormSubmit={onEmailFormSubmit}
                buttonText={
                  buttonText ||
                  translateMessage({
                    id: LABEL_KEYS['media-ui-login_continue'],
                    defaultMessage: defaultMessages['media-ui-login_continue'],
                  })
                }
                isAutoOptedIn={marketingConsent}
                loading={isLoading || !regUIClient}
                centeredLayout={centeredLayout}
                onEmailChange={setCurrentEmail}
                defaultValue={currentEmail}
                countryCode={countryCode}
                ref={ref}
                buttonType={buttonType === 'primary' ? 'primary' : 'outlined'}
                autoFocus={autoFocus}
                theme={theme === 'dark' ? 'dark' : 'light'}
                setGlobalMarketingConsent={setGlobalMarketingConsent}
                error={error?.email}
                fullWidth={fullWidth}
              />
            )}
          </div>
        </div>
      );
    } else if (loginStep === LOGIN_STEP.CHOOSE_YOUR_METHOD) {
      return (
        <EnterPasswordOrVerificationCode
          theme={theme}
          thinking={isLoading}
          error={error}
          email={emailToVerify}
          regUIClient={regUIClient}
          marketingConsent={marketingConsent}
          onEmailChange={() => setLoginStep(LOGIN_STEP.EMAIL_SUBMISSION)}
          onPasswordLogin={onPasswordLogin}
          codeReSent={codeReSent}
          onResendEmail={onResendEmail}
          onVerificationCodeInputStarting={onVerificationCodeInputStarting}
          onVerificationCodeInputComplete={onVerificationCodeInputComplete}
          onVerificationCodeSubmit={onVerificationCodeSubmit}
          verificationCodeSent={verificationCodeSent}
        />
      );
    } else if (loginStep === LOGIN_STEP.VERIFICATION_CODE) {
      return (
        <div className={styles.verificationInputContainer}>
          <VerificationCodeInput
            marketingConsent={marketingConsent}
            email={emailToVerify}
            onEmailChange={changeEmail}
            onResendEmail={onResendEmail}
            codeReSent={codeReSent}
            ref={refCodeForm}
            allowEditEmail={allowEditEmail}
            theme={theme === 'dark' ? 'dark' : 'light'}
            onVerificationCodeSubmit={onVerificationCodeSubmit}
            setIsLoggedIn={setIsLoggedIn}
            googleTracking={googleTracking}
            nr={nr}
            regUIClient={regUIClient}
            leadSource={leadSource}
          />
        </div>
      );
    }

    // default case that should never be reached
    return <div />;
  }
);
